import React from 'react';
import { Box, Typography } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { makeStyles } from 'tss-react/mui';
import type { ProposalId } from '@shared/models/proposals.model';
import { useSelectedVersion } from '@client/store/ui';
import { getConversionFactorValue } from '@shared/helpers/conversion-factors.helpers';
import { getDisplayNumber } from '@shared/helpers/display/get-display-number.helper';
import { useResultsPerGFA } from '@features/results/use-results.hooks';
import { Icon } from '@ui/Icon';
import { useProposalsResultRecords } from '../use-proposals.hooks';
import { useDisplayCurrency } from '@features/currency/selected-display-currency.hook';
import { useCo2eVisible } from '@features/results/co2e-visible.hook';
import { useActionsForProposal } from '../hooks/use-actions-for-proposal.hooks';
import { KineticNumberDisplay } from '@ui/KineticNumberDisplay';

const CARBON_POSITIVE_BG = '#c8ffd6';
const CARBON_POSITIVE_COLOR = '#266b37';
const CARBON_NEGATIVE_BG = '#fbe7ea';
const CARBON_NEGATIVE_COLOR = '#803943';
const formatCompactNumber = getDisplayNumber.withOptions({
  notation: 'compact',
});

/** Letter spacing for the large KPI value figures (KpiCard); reuse for action-row KPI numbers. */
export const KPI_CARD_VALUE_LETTER_SPACING = '-0.01em' as const;

type ArrowDirection = 'up' | 'down';
type ArrowVariant = 'carbon' | 'neutral';

type KpiCardProps = {
  value: number | null;
  label?: string;
  prefixIcon?: React.ReactNode;
  suffixIcon?: React.ReactNode;
  arrow?: ArrowDirection;
  arrowVariant?: ArrowVariant;
  fontWeight?: 400 | 600;
};

/**
 * Displays one proposal KPI and animates changes to its numeric value.
 */
const KpiCard: React.FC<KpiCardProps> = ({
  value,
  label,
  prefixIcon,
  suffixIcon,
  arrow,
  arrowVariant = 'neutral',
  fontWeight = 400,
}) => {
  const { classes } = useStyles();

  const isNegative = arrow === 'up';
  const chipBg = isNegative ? CARBON_NEGATIVE_BG : CARBON_POSITIVE_BG;
  const chipColor = isNegative ? CARBON_NEGATIVE_COLOR : CARBON_POSITIVE_COLOR;

  const ArrowIcon = arrow === 'up' ? ArrowUpwardIcon : ArrowDownwardIcon;

  return (
    <Box className={classes.card}>
      <Box className={classes.valueRow}>
        {arrow && arrowVariant === 'carbon' && (
          <Box
            className={classes.arrowChip}
            sx={{ backgroundColor: chipBg, color: chipColor }}
          >
            <ArrowIcon sx={{ fontSize: 18 }} />
          </Box>
        )}
        {arrow && arrowVariant === 'neutral' && (
          <Box className={classes.arrowNeutral}>
            <ArrowIcon sx={{ fontSize: 20 }} />
          </Box>
        )}
        <Typography
          sx={{
            fontSize: 34,
            fontWeight,
            letterSpacing: KPI_CARD_VALUE_LETTER_SPACING,
            lineHeight: 1.15,
            minHeight: '1.15em',
            opacity: value === null ? 0.3 : 0.8,
            whiteSpace: 'nowrap',
          }}
        >
          {value !== null ? (
            <KineticNumberDisplay
              value={Math.abs(value)}
              format={formatCompactNumber}
            />
          ) : (
            '—'
          )}
        </Typography>
      </Box>
      <Box className={classes.labelRow}>
        {prefixIcon}
        {label && <span className={classes.label}>{label}</span>}
        {suffixIcon}
      </Box>
    </Box>
  );
};

const VALUE_ROW_MIN_HEIGHT = 42;
const LABEL_ROW_MIN_HEIGHT = 18;

const useStyles = makeStyles()((theme) => ({
  card: {
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    height: '100%',
    justifyContent: 'flex-start',
    minHeight: 0,
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  valueRow: {
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'row',
    flexShrink: 0,
    gap: theme.spacing(2),
    justifyContent: 'center',
    minHeight: VALUE_ROW_MIN_HEIGHT,
    padding: theme.spacing(0.5, 1),
    width: '100%',
  },
  arrowChip: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
    width: 24,
    height: 24,
    flexShrink: 0,
    border: '1px solid rgba(0,0,0,0.1)',
    opacity: 0.8,
  },
  arrowNeutral: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 8,
    height: 24,
    flexShrink: 0,
    opacity: 0.8,
  },
  labelRow: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    flexShrink: 0,
    gap: theme.spacing(0.75),
    justifyContent: 'center',
    marginTop: theme.spacing(-1),
    minHeight: LABEL_ROW_MIN_HEIGHT,
    opacity: 0.6,
    width: '100%',
  },
  label: {
    fontSize: 11,
    fontWeight: 500,
    letterSpacing: '0.1em',
    lineHeight: 'normal',
    textTransform: 'uppercase' as const,
  },
}));

type ProposalKpiSectionProps = {
  proposalId: ProposalId;
};

const useContainerStyles = makeStyles<{ twoRowLayout: boolean }>()(
  (theme, { twoRowLayout }) => ({
    container: {
      alignContent: 'stretch',
      boxSizing: 'border-box',
      columnGap: theme.spacing(2),
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gridTemplateRows: twoRowLayout ? '1fr 1fr' : '1fr',
      margin: 0,
      minHeight: 0,
      padding: theme.spacing(5, 2),
      rowGap: 0,
    },
  }),
);

const CO2_ICON = <Icon category="costEmission" icon="kgCO2e" size={18} />;
const SEK_ICON = <Icon category="currency" icon="SEK" size={18} />;

export const ProposalKpiSection: React.FC<ProposalKpiSectionProps> = ({
  proposalId,
}) => {
  const displayCurrency = useDisplayCurrency();
  const co2eVisible = useCo2eVisible();
  const showCostKpis = displayCurrency.isEnabled;
  const { classes } = useContainerStyles({
    twoRowLayout: showCostKpis || co2eVisible.isEnabled,
  });
  const version = useSelectedVersion();
  const records = useProposalsResultRecords();
  const actions = useActionsForProposal(proposalId);

  const baselineResults = version
    ? (records[version.id]?.[version.id] ?? {})
    : {};
  const proposalResults = version
    ? (records[proposalId]?.[version.id] ?? {})
    : {};

  const baselineResultsPerGFA = useResultsPerGFA(baselineResults);
  const proposalResultsPerGFA = useResultsPerGFA(proposalResults);

  const baselineCo2 = getConversionFactorValue(baselineResultsPerGFA, 'co2e');
  const proposalCo2 = getConversionFactorValue(proposalResultsPerGFA, 'co2e');
  const baselineCost = getConversionFactorValue(baselineResultsPerGFA, 'sek');
  const proposalCost = getConversionFactorValue(proposalResultsPerGFA, 'sek');

  const hasActions = actions.length > 0;

  const deltaCo2 = hasActions ? proposalCo2 - baselineCo2 : null;
  const deltaCost = hasActions ? proposalCost - baselineCost : null;
  const costPerCo2 =
    deltaCo2 !== null && deltaCo2 !== 0 && deltaCost !== null
      ? deltaCost / deltaCo2
      : null;

  const co2Arrow: ArrowDirection | undefined =
    deltaCo2 !== null
      ? deltaCo2 < 0
        ? 'down'
        : deltaCo2 > 0
          ? 'up'
          : undefined
      : undefined;
  const costArrow: ArrowDirection | undefined =
    deltaCost !== null ? (deltaCost <= 0 ? 'down' : 'up') : undefined;

  return (
    <Box className={classes.container}>
      {co2eVisible.isEnabled && (
        <KpiCard
          value={deltaCo2}
          label="/ m²"
          prefixIcon={CO2_ICON}
          arrow={co2Arrow}
          arrowVariant="carbon"
          fontWeight={600}
        />
      )}
      {showCostKpis && (
        <KpiCard
          value={deltaCost}
          label="/ m²"
          prefixIcon={SEK_ICON}
          arrow={costArrow}
          arrowVariant="neutral"
        />
      )}
      <KpiCard value={hasActions ? actions.length : null} label="actions" />
      {showCostKpis && co2eVisible.isEnabled && (
        <KpiCard
          value={costPerCo2}
          prefixIcon={SEK_ICON}
          label="/"
          suffixIcon={CO2_ICON}
        />
      )}
    </Box>
  );
};
